import { coalescedGet, http, invalidateCoalescedGets, request } from "@/lib/http";
import type {
  Device,
  DeviceDashboardResponse,
  PortAvailability,
  ProductionHistory,
  ProductionHistoryWindow,
  ProductionTracker,
} from "./types";

/** GET /api/devices/discovered -> Device[] (worker MQTT). */
export function listDiscoveredDevices(signal?: AbortSignal): Promise<Device[]> {
  return http
    .get<Device[] | { devices?: Device[] }>("/api/devices/discovered", { signal })
    .then((raw) => {
      if (Array.isArray(raw)) return raw;
      if (raw && Array.isArray(raw.devices)) return raw.devices;
      return [];
    });
}

/** GET /api/devices/:deviceId -> Device. Sempre use Device.id, nao mqttId. */
/**
 * GET /api/devices/:deviceId/dashboard -> payload agregado.
 *
 * Coalescido: Sidebar e a tela do workflow pedem o mesmo dashboard no mesmo
 * tick (mount/focus). Compartilhar a requisição em voo evita chamadas
 * duplicadas sem mudar frescura.
 */
export function getDeviceDashboard(
  deviceId: string,
  signal?: AbortSignal
): Promise<DeviceDashboardResponse> {
  return coalescedGet<DeviceDashboardResponse>(
    `/api/devices/${encodeURIComponent(deviceId)}/dashboard`,
    signal
  );
}

export function getProductionHistory(
  deviceId: string,
  portIndex: number,
  window: ProductionHistoryWindow,
  signal?: AbortSignal,
): Promise<ProductionHistory> {
  const query = new URLSearchParams({ portIndex: String(portIndex), window });
  return http.get<ProductionHistory>(
    `/api/devices/${encodeURIComponent(deviceId)}/production/history?${query}`,
    { signal },
  );
}

export function getProductionHistories(deviceId: string, portIndices: number[],
  window: ProductionHistoryWindow, signal?: AbortSignal): Promise<ProductionHistory[]> {
  const query = new URLSearchParams({ portIndices: [...new Set(portIndices)].sort((a, b) => a - b).join(","), window });
  return coalescedGet<ProductionHistory[]>(
    `/api/devices/${encodeURIComponent(deviceId)}/production/histories?${query}`, signal);
}

export function getAvailability(
  deviceId: string,
  portIndex: number,
  window: ProductionHistoryWindow,
  signal?: AbortSignal,
): Promise<PortAvailability> {
  const query = new URLSearchParams({ portIndex: String(portIndex), window });
  return http.get<PortAvailability>(
    `/api/devices/${encodeURIComponent(deviceId)}/availability?${query}`,
    { signal },
  );
}

function productionTrackerPath(
  workspaceId: string,
  deviceId: string,
  portIndex: number,
): string {
  return `/api/workspaces/${encodeURIComponent(workspaceId)}/devices/${encodeURIComponent(deviceId)}/ports/${portIndex}/production`;
}

export function getProductionTracker(
  workspaceId: string,
  deviceId: string,
  portIndex: number,
  signal?: AbortSignal,
): Promise<ProductionTracker> {
  return http.get<ProductionTracker>(
    productionTrackerPath(workspaceId, deviceId, portIndex),
    { signal },
  );
}

export function configureProductionTracker(
  workspaceId: string,
  deviceId: string,
  portIndex: number,
  input: { target: number | null; unit?: string | null },
  signal?: AbortSignal,
): Promise<ProductionTracker> {
  return http.put<ProductionTracker>(
    productionTrackerPath(workspaceId, deviceId, portIndex),
    input,
    { signal },
  );
}

export function resetProductionTracker(
  workspaceId: string,
  deviceId: string,
  portIndex: number,
  actorId = "operator-local",
): Promise<ProductionTracker> {
  return http.post<ProductionTracker>(
    `${productionTrackerPath(workspaceId, deviceId, portIndex)}/reset`,
    { actorId },
  );
}

/** GET /api/devices/:deviceId/meta/current -> CurrentMeta cru (metaJson como string). */
export interface ClaimDeviceInput {
  deviceId?: string;
  mqttId?: string;
  workspaceId: string;
  claimedByUserId: string;
  claimedAt?: string;
}

export interface ClaimDeviceResponse {
  claimId: string;
  deviceId: string;
  workspaceId: string;
  claimedByUserId: string;
  claimedAt: string;
}

/** POST /api/devices/claim. */
export function claimDevice(
  input: ClaimDeviceInput,
  signal?: AbortSignal
): Promise<ClaimDeviceResponse> {
  return http.post<ClaimDeviceResponse>("/api/devices/claim", input, { signal });
}

/**
 * PATCH /api/devices/:deviceId — troca o NOME FANTASIA.
 *
 * O `mqttId` nao entra aqui e nao muda nunca: ele e a identidade de rede, e a
 * ACL do broker prende o topico a ele. Renomear ali faria a placa publicar num
 * topico sem permissao — e em QoS 0 o sintoma seria "o dispositivo sumiu", sem
 * erro nenhum.
 *
 * `null` apaga o nome e a interface volta a mostrar o `mqttId`.
 */
export function renomearDevice(
  deviceId: string,
  nomeFantasia: string | null,
  signal?: AbortSignal,
): Promise<Device> {
  return request<{ device: Device }>(
    `/api/devices/${encodeURIComponent(deviceId)}`,
    { method: "PATCH", body: JSON.stringify({ nomeFantasia }), signal },
  ).then((raw) => {
    invalidateCoalescedGets((path) => path.startsWith("/api/workspaces/") || path.startsWith("/api/devices/"));
    return raw.device;
  });
}

/** POST /api/devices/:deviceId/unclaim — desassocia o device do workflow. */
export function unclaimDevice(
  deviceId: string,
  signal?: AbortSignal
): Promise<{ ok: boolean; deviceId: string }> {
  return http.post<{ ok: boolean; deviceId: string }>(
    `/api/devices/${encodeURIComponent(deviceId)}/unclaim`,
    {},
    { signal }
  );
}

/**
 * GET /api/workspaces/:workspaceId/devices -> Device[].
 *
 * Coalescido: três caminhos chamam isto (listWorkspaceMachines,
 * getMachineDashboard, getMachineSidebarSummary) e no mount disparam juntos.
 * Compartilhar a requisição em voo colapsa a rajada numa única chamada.
 */
export function listWorkspaceDevices(
  workspaceId: string,
  signal?: AbortSignal
): Promise<Device[]> {
  return coalescedGet<Device[] | { devices?: Device[] }>(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/devices`,
    signal
  )
    .then((raw) => {
      if (Array.isArray(raw)) return raw;
      if (raw && Array.isArray(raw.devices)) return raw.devices;
      return [];
    });
}

/**
 * Credencial do dispositivo no broker.
 *
 * A ORDEM SURPREENDE quem conhece o fluxo antigo: a credencial vem ANTES de o
 * dispositivo existir para o sistema. Sem ela a placa nao conecta, e sem
 * conectar ela nunca publica — logo, nunca aparece em "descobertos". "Ligue a
 * placa e ela aparece" deixou de valer quando o broker passou a exigir
 * autenticacao.
 */
export interface CredencialDeDispositivo {
  mqttId: string;
  /** Existe UMA vez. O broker guarda so o hash; nao ha rota que a devolva. */
  senha: string;
  /** `true` = a placa ja tinha credencial e a senha foi trocada. */
  jaExistia: boolean;
}

export function provisionarCredencial(
  workspaceId: string,
  mqttId: string,
): Promise<CredencialDeDispositivo> {
  return http.post<CredencialDeDispositivo>(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/device-credentials`,
    { mqttId },
  );
}

export function revogarCredencial(
  workspaceId: string,
  mqttId: string,
): Promise<{ mqttId: string; revogado: boolean }> {
  return request<{ mqttId: string; revogado: boolean }>(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/device-credentials/${encodeURIComponent(mqttId)}`,
    { method: "DELETE" },
  );
}
